// ============================================================================
// AffichagesPage — liste des affichages (E1d, F-300..F-303) — /affichages
// ============================================================================
// Portage du PoC (dashboard)/affichages/page.tsx au standard maison :
//   - lecture OUVERTE a tout role GEST interne (M-302) ; le 403 corporate-sans-GEST
//     (Q1) donne l'ecran de refus i18n ;
//   - filtre Ouverts/Tous en GsSegmented deep-linkable ?status=all (F-300) ;
//   - alerte ?error= mappee i18n (F-301, GA-21) ;
//   - « Demande d'affichage » (F-303) si writableTeams.length>0 (admin/director
//     scoped, garde serveur) -> NewPostingDialog ;
//   - grille PoC -> DataTable MAISON + drill-in par ligne (ecart standard #2) :
//     titre (+ indicateur piece jointe), equipe, statut, publie le, echeance
//     (badge « echeance proche » si <7j, helper isDeadlineSoon reutilise) ;
//   - etats skeleton / empty / error / forbidden (patterns E1b/E3a).

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageHeader, EmptyState, DataTable, GsSegmented, SkeletonCard, QueryError,
  GS_COMPONENTS, type Column,
} from '@gs/ui';
import { ClipboardList, Paperclip, Plus, ShieldAlert } from 'lucide-react';
import { ApiForbiddenError } from '@gs/api-client';
import { useAuth } from '@gs/auth';
import { useTranslation } from '../../i18n';
import { usePostings, type PostingSummary } from '../../hooks/usePostings';
import { NewPostingDialog } from '../../components/affichages/NewPostingDialog';
import { PostingStatusBadge } from './PostingStatusBadge';
import { formatDate, formatDateOnly, isDeadlineSoon } from './format';

const KNOWN_ERRORS = ['invalid', 'forbidden', 'notfound', 'server'];

/** Ecran de refus (Q1 corporate-sans-GEST) — l'autorite est le 403 serveur. */
export function PostingsForbidden() {
  const { t } = useTranslation();
  return (
    <EmptyState
      icon={<ShieldAlert size={48} aria-hidden="true" />}
      title={t('gest:affichages.forbidden.title')}
      description={t('gest:affichages.forbidden.description')}
    />
  );
}

export default function AffichagesPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialogOpen, setDialogOpen] = useState(false);

  const showAll = searchParams.get('status') === 'all'; // F-300 deep-link
  const errorCode = searchParams.get('error'); // F-301 deep-link

  const listQ = usePostings(showAll, !!user);
  const forbidden = listQ.isError && listQ.error instanceof ApiForbiddenError;

  function setError(code: string) {
    const next = new URLSearchParams(searchParams);
    next.set('error', code);
    setSearchParams(next);
  }

  function setStatusFilter(value: string) {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.set('status', 'all');
    else next.delete('status');
    next.delete('error');
    setSearchParams(next);
  }

  // ─── Colonnes DataTable (titre, equipe, statut, publie le, echeance) ─────────
  const columns: Column<PostingSummary>[] = [
    {
      key: 'title',
      header: t('gest:affichages.columns.title'),
      render: (p) => (
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-semibold text-slate-900 dark:text-slate-100">{p.title}</span>
          {p.hasAttachment ? (
            <Paperclip size={14} aria-label={t('gest:affichages.attachment.present')} className="shrink-0 text-slate-400" />
          ) : null}
        </div>
      ),
    },
    {
      key: 'team',
      header: t('gest:affichages.columns.team'),
      render: (p) =>
        p.teamName ?? <span className="text-slate-400 dark:text-slate-500">{t('gest:affichages.noTeam')}</span>,
    },
    {
      key: 'status',
      header: t('gest:affichages.columns.status'),
      render: (p) => <PostingStatusBadge status={p.status} />,
    },
    {
      key: 'postedAt',
      header: t('gest:affichages.columns.postedAt'),
      className: 'tabular-nums',
      render: (p) => formatDate(i18n.language, p.postedAt),
    },
    {
      key: 'deadline',
      header: t('gest:affichages.columns.deadline'),
      render: (p) =>
        p.deadline ? (
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-chip font-medium ${
            isDeadlineSoon(p.deadline)
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
              : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
          }`}>
            {formatDateOnly(i18n.language, p.deadline)}
          </span>
        ) : (
          <span className="text-slate-400 dark:text-slate-500">—</span>
        ),
    },
  ];

  const canCreate = !!listQ.data && listQ.data.writableTeams.length > 0;

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('gest:affichages.title')}
        onRefresh={() => void listQ.refetch()}
        refreshLoading={listQ.isFetching}
        actions={
          <GsSegmented
            ariaLabel={t('gest:affichages.filter.label')}
            value={showAll ? 'all' : 'open'}
            onChange={setStatusFilter}
            options={[
              { value: 'open', label: t('gest:affichages.filter.open') },
              { value: 'all', label: t('gest:affichages.filter.all') },
            ]}
          />
        }
      />

      {errorCode ? (
        // Bannière d'état ?error= — liseré gauche (SM-5), pas fond teinté plein.
        <div
          role="alert"
          className="rounded-xl border border-slate-200 border-l-3 border-l-red-500 bg-white px-4 py-3 text-field text-red-700 dark:border-slate-700 dark:border-l-red-500 dark:bg-slate-800 dark:text-red-300"
        >
          {t(`gest:affichages.errors.${KNOWN_ERRORS.includes(errorCode) ? errorCode : 'server'}`)}
        </div>
      ) : null}

      {forbidden ? (
        <PostingsForbidden />
      ) : listQ.isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      ) : listQ.isError ? (
        <QueryError message={t('gest:affichages.loadError')} onRetry={() => void listQ.refetch()} />
      ) : listQ.data ? (
        <>
          {canCreate ? (
            <div>
              <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className={`${GS_COMPONENTS.btnAction} inline-flex items-center gap-1.5`}
              >
                <Plus size={16} aria-hidden="true" />
                {t('gest:affichages.new')}
              </button>
            </div>
          ) : null}

          {listQ.data.postings.length === 0 ? (
            <EmptyState
              icon={<ClipboardList size={48} aria-hidden="true" />}
              title={showAll ? t('gest:affichages.emptyAll') : t('gest:affichages.emptyOpen')}
            />
          ) : (
            <div className={`${GS_COMPONENTS.card} overflow-hidden p-0`}>
              <DataTable
                columns={columns}
                data={listQ.data.postings}
                rowKey={(p) => p.id}
                onRowClick={(p) => navigate(`/affichages/${p.id}`)}
              />
            </div>
          )}
        </>
      ) : null}

      {canCreate ? (
        <NewPostingDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onError={setError}
          writableTeams={listQ.data?.writableTeams ?? []}
        />
      ) : null}
    </div>
  );
}
